import React from 'react';
import { Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import App from './App';
import { DashboardPage } from './pages/DashboardPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AboutPage } from './pages/AboutPage';
import { HelpPage } from './pages/HelpPage';
import { TermsPage } from './pages/TermsPage';
import { ContactPage } from './pages/ContactPage';
import { DisclaimerPage } from './pages/DisclaimerPage';
import { PricingPage } from './pages/PricingPage';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { useContent } from './hooks/useContent';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { AiPromptIndexPage } from './pages/AiPromptIndexPage';
import { PromptCategoryPage } from './pages/PromptCategoryPage';
import { DynamicRouteHandler } from './router/DynamicRouteHandler';
import { AuthModal } from './components/AuthModal';
import { useAuth } from './hooks/useAuth';
import { ShoeBrandPage } from './pages/ShoeBrandPage';
import { CoffeeBrandPage } from './pages/CoffeeBrandPage';
import { BrandAuditPage } from './pages/BrandAuditPage';
import { BlogIndexPage } from './pages/BlogIndexPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { BlogCategoryPage } from './pages/BlogCategoryPage';
import { BlogTagPage } from './pages/BlogTagPage';
import { LandingPageTemplate } from './pages/LandingPageTemplate';
import { IndustriesIndexPage } from './pages/IndustriesIndexPage';
import { InstagramAuditPage } from './pages/socialMedia/InstagramAuditPage';
import { INDUSTRY_LANDING_CONFIGS } from './contexts/industryPagesData';

const Layout: React.FC = () => (
  <div className="flex flex-col min-h-screen bg-white">
    <Header />
    <main className="flex-grow w-full">
      <Outlet />
    </main>
    <Footer />
  </div>
);

const MainRouter: React.FC = () => {
  return (
    <Routes>
      {/* Static routes with dedicated components */}
      <Route path="/" element={<App />} />
      <Route path="/dashboard/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/about/" element={<AboutPage />} />
      <Route path="/help/" element={<HelpPage />} />
      <Route path="/terms/" element={<TermsPage />} />
      <Route path="/contact/" element={<ContactPage />} />
      <Route path="/disclaimer/" element={<DisclaimerPage />} />
      <Route path="/pricing/" element={<PricingPage />} />
      <Route path="/reset-password/" element={<ResetPasswordPage />} />

      {/* Industries Directory Hub */}
      <Route path="/industries/" element={<IndustriesIndexPage />} />

      {/* Specific Industry Landing Pages */}
      <Route path="/shoe-brand-name-generator/" element={<ShoeBrandPage />} />
      <Route path="/coffee-brand-name-generator/" element={<CoffeeBrandPage />} />
      {INDUSTRY_LANDING_CONFIGS.filter(
        (cfg) => cfg.key !== 'shoeBrand' && cfg.key !== 'coffeeBrand'
      ).map((cfg) => (
        <Route
          key={cfg.key}
          path={cfg.slug}
          element={<LandingPageTemplate contentKey={cfg.key} />}
        />
      ))}

      {/* AI Prompt Generator Routes */}
      <Route path="/ai-prompt-generator/" element={<AiPromptIndexPage />} />
      <Route path="/ai-prompt-generator/:categoryKey/" element={<PromptCategoryPage />} />

      {/* Public Brand & Social Media Audit Tools */}
      <Route path="/brand-audit/" element={<BrandAuditPage />} />
      <Route path="/social-media-audit/" element={<InstagramAuditPage />} />
      <Route path="/social-media-audit/instagram/" element={<InstagramAuditPage />} />
      <Route path="/instagram-audit/" element={<InstagramAuditPage />} />

      {/* Blog Routes */}
      <Route path="/blog/" element={<BlogIndexPage />} />
      <Route path="/blog/category/:category/" element={<BlogCategoryPage />} />
      <Route path="/blog/tag/:tag/" element={<BlogTagPage />} />
      <Route path="/blog/:slug/" element={<BlogPostPage />} />

      {/* Fallback route for dynamic pages and 404 */}
      <Route path="*" element={<DynamicRouteHandler />} />
    </Routes>
  );
};

export const AppRoutes: React.FC = () => {
  const { isLoaded } = useContent();
  const { modalView, setModalView } = useAuth();
  const closeModal = () => setModalView('none');

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const { pathname } = location;
    // Don't redirect for the root path, paths with file extensions, or paths that already have a trailing slash.
    if (pathname.length > 1 && !pathname.endsWith('/') && !/\.[^/.]+$/.test(pathname)) {
      // Use replace to avoid adding a new entry to the history stack
      navigate(`${pathname}/`, { replace: true, state: location.state });
    }
  }, [location, navigate]);

  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          {isLoaded ? (
            <Route path="/*" element={<MainRouter />} />
          ) : (
            <Route
              path="/*"
              element={
                <div className="flex items-center justify-center h-screen bg-white">
                  <div className="flex items-center text-gray-600">
                    <svg
                      className="animate-spin h-8 w-8 text-[#4F46E5]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="ml-3 text-lg">Initializing BrandGenius...</span>
                  </div>
                </div>
              }
            />
          )}
        </Route>
      </Routes>

      <AuthModal
        modalView={modalView}
        setModalView={setModalView}
        closeModal={closeModal}
      />
    </>
  );
};
